import { cn } from "@newxsystems/shared";
import { getComponent } from "../../../../client";
import { RavioliCommonContent } from "../../../../modules/content/components/RavioliCommonContent/RavioliCommonContent";
import { RavioliMedia } from "../../../../modules/content/components/RavioliMedia/RavioliMedia";
import { RavioliRichText } from "../../../../modules/content/components/RavioliRichText/RavioliRichText";
import { RavioliSection } from "../../../../modules/content/components/RavioliSection/RavioliSection";
import type { ITeamMemberGridBlockProps } from "./TeamMemberGridBlock.props";

const columns = {
  1: "grid-cols-1",
  2: "grid-cols-1 sm:grid-cols-2",
  3: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
  4: "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4",
} as const;

export const TeamMemberGridBlock = ({
  alignment,
  anchorId,
  buttons,
  columnCount,
  description,
  eyebrow,
  heading,
  items,
  parentClass,
  spacing,
}: ITeamMemberGridBlockProps) => {
  if (!items?.length) return null;
  const Link = getComponent("Link");
  const effectiveColumnCount = Math.min(columnCount || 3, items.length) as 1 | 2 | 3 | 4;

  return (
    <RavioliSection className={parentClass} id={anchorId} spacing={spacing}>
      <div className="grid gap-8">
        <RavioliCommonContent
          alignment={alignment}
          buttons={buttons}
          description={description}
          eyebrow={eyebrow}
          heading={heading}
        />
        <div className={cn("grid gap-4", columns[effectiveColumnCount])}>
          {items.map((member, index) => (
            <article
              className="ravioli-motion-travel group border border-primary bg-surface p-2 transition-[box-shadow,transform] duration-200 ease-out hover:-translate-y-1 hover:shadow-ravioli-action-sm"
              key={index}
            >
              <RavioliMedia
                aspectRatio="3:4"
                // className="bg-ink-muted"
                imageClassName="transition-transform duration-300 ease-out group-hover:scale-[1.015]"
                media={member.portrait.image}
                sizes="(min-width: 1024px) 25vw, (min-width: 640px) 50vw, 100vw"
              />
              <div className="border-t border-primary px-4 pb-4 pt-5">
                <h3 className="font-ravioli-display text-3xl font-semibold leading-[1.05]">
                  {member.name}
                </h3>
                {member.role ? (
                  <p className="mt-2 font-ravioli-body text-[0.8125rem] font-semibold text-primary">
                    {member.role}
                  </p>
                ) : null}
                {member.biography ? (
                  <RavioliRichText
                    className="mt-4 text-base text-ink-muted md:text-lg"
                    content={member.biography}
                  />
                ) : null}
                {member.links?.length ? (
                  <div className="mt-5 flex flex-wrap gap-4 border-t border-line pt-3">
                    {member.links.map((link, linkIndex) => (
                      <Link
                        aria-label={link.accessibilityLabel || undefined}
                        className="inline-flex min-h-11 items-center font-ravioli-mono text-xs text-primary underline underline-offset-4 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                        href={link.href}
                        key={linkIndex}
                        rel={link.openInNewTab ? "noopener noreferrer" : undefined}
                        target={link.openInNewTab ? "_blank" : undefined}
                      >
                        {link.label}
                      </Link>
                    ))}
                  </div>
                ) : null}
              </div>
            </article>
          ))}
        </div>
      </div>
    </RavioliSection>
  );
};

TeamMemberGridBlock.displayName = "Block::TeamMemberGridBlock";
